<template>
  <!-- Tarjeta de un borrador dentro del hilo del asistente. Genérica: lo propio
       de cada módulo (campos, claves del PATCH, formulario) sale de
       ASSISTANT_MODULES. Se actualiza en el lugar: pregunta → resumen → cargado. -->
  <article class="dc" :class="[`is-${draft.status}`, { 'is-busy': busy }]">
    <header class="dc-head">
      <span class="dc-kind" :class="`dc-kind--${draft.kind || 'other'}`">
        <component :is="icon" :size="18" weight="duotone" />
      </span>
      <div class="dc-head__text">
        <span class="dc-head__title">{{ title }}</span>
        <span class="dc-head__file" :title="draft.file_name">{{ shortFileName(draft.file_name) }}</span>
      </div>
      <span class="dc-status">{{ $t(`assistant.draft.status.${draft.status}`) }}</span>
    </header>

    <!-- Planilla (Excel/CSV): vista previa en tabla -->
    <DraftSheet
      v-if="isSheet"
      :draft="draft"
      :busy="busy"
      :read-only="readOnly"
      @patch="(changes) => patch(changes)"
    />

    <!-- Lo que leyó -->
    <dl v-else-if="readingFacts.length" class="dc-facts">
      <template v-for="f in readingFacts" :key="f.key">
        <dt>{{ f.label }}</dt><dd>{{ f.value }}</dd>
      </template>
    </dl>
    <div class="dc-links">
      <a v-if="receiptUrl" :href="receiptUrl" target="_blank" rel="noopener" class="dc-link">
        <PhArrowSquareOut :size="14" /> {{ $t('assistant.draft.viewFile') }}
      </a>
      <span v-if="draft.read_method === 'xml'" class="dc-note">{{ $t('assistant.draft.readXml') }}</span>
    </div>

    <!-- Respuestas ya dadas -->
    <ol v-if="trail.length" class="dc-trail">
      <li v-for="(s, i) in trail" :key="i"><span>{{ s.q }}</span> <b>{{ s.a }}</b></li>
    </ol>

    <p v-if="error" class="dc-error" role="alert">{{ error }}</p>

    <!-- ── Pendiente con pregunta ── -->
    <section v-if="draft.status === 'pending' && question" class="dc-q">
      <p class="dc-q__text">{{ question.text }}</p>
      <p v-if="remaining > 1" class="dc-q__steps">{{ $t('assistant.draft.remaining', { n: remaining }) }}</p>
      <template v-if="!readOnly">
        <div v-if="qOptions.length && qOptions.length <= 4" class="dc-chips">
          <button
            v-for="o in qOptions" :key="String(o.value)" type="button" class="dc-chip"
            :class="{ 'is-recent': o.recent }" :disabled="busy" @click="answer(o.value, o.label)"
          >
            {{ o.label }}<span v-if="o.recent" class="dc-chip__tag">{{ $t('assistant.draft.recent') }}</span>
          </button>
        </div>
        <form v-else-if="qOptions.length" class="dc-inline" @submit.prevent="answerSelect">
          <DsSelect v-model="answerModel" :aria-label="question.text">
            <option v-for="o in qOptions" :key="String(o.value)" :value="String(o.value)">{{ o.label }}</option>
          </DsSelect>
          <DsButton type="submit" size="sm" :disabled="busy || answerModel === ''">{{ $t('assistant.draft.choose') }}</DsButton>
        </form>
        <form v-else-if="qInput" class="dc-inline" @submit.prevent="answerInput">
          <DsInput v-model="answerModel" :type="qInput" :min="qInput === 'number' ? 0 : undefined" :step="qInput === 'number' ? 'any' : undefined" :aria-label="question.text">
            <template v-if="qInput === 'number' && reading.unit" #suffix>{{ unitLabel(reading.unit) }}</template>
          </DsInput>
          <DsButton type="submit" size="sm" :disabled="busy || !validInput">{{ $t('assistant.draft.send') }}</DsButton>
        </form>
        <!-- La entidad no existe todavía en la empresa -->
        <div v-else-if="moduleCfg?.settingsRoute" class="dc-chips">
          <button type="button" class="dc-chip" @click="$emit('navigate', moduleCfg.settingsRoute)">{{ $t('assistant.draft.createIt') }}</button>
        </div>
      </template>
    </section>

    <!-- ── Pendiente, sin preguntas: resumen ── -->
    <section v-else-if="draft.status === 'pending'" class="dc-summary">
      <dl v-if="!isSheet && targetFacts.length" class="dc-facts dc-facts--target">
        <template v-for="f in targetFacts" :key="f.key">
          <dt>{{ f.label }}</dt><dd>{{ f.value }}</dd>
        </template>
      </dl>
      <!-- Por qué eligió ese destino (§ 3 `basis`) -->
      <p v-if="!isSheet && basisText" class="dc-basis"><PhInfo :size="14" /> {{ basisText }}</p>
      <ul v-if="!isSheet && draft.warnings?.length" class="dc-warnings">
        <li v-for="(w, i) in draft.warnings" :key="i" :class="{ 'is-strong': BLOCKING_WARNINGS.has(w.code) }">
          <PhWarning :size="16" weight="fill" /> <span>{{ w.message }}</span>
        </li>
      </ul>

      <template v-if="!readOnly">
        <form v-if="editing" class="dc-edit" @submit.prevent="saveEdit">
          <label v-for="f in visibleEditFields" :key="f.key">
            {{ $t(`assistant.fields.${f.label}`) }}
            <DsInput v-if="f.input" v-model="edit[f.key]" :type="f.input" :min="f.input === 'number' ? 0 : undefined" step="any">
              <template v-if="f.unitFrom && reading[f.unitFrom]" #suffix>{{ unitLabel(reading[f.unitFrom]) }}</template>
            </DsInput>
            <DsSelect v-else v-model="edit[f.key]">
              <option value="">—</option>
              <option v-for="o in editOptions(f)" :key="String(o.value)" :value="String(o.value)">{{ o.label }}</option>
            </DsSelect>
          </label>
          <div class="dc-actions">
            <DsButton type="submit" size="sm" :loading="busy">{{ $t('assistant.draft.save') }}</DsButton>
            <DsButton variant="secondary" size="sm" @click="editing = false">{{ $t('assistant.draft.cancel') }}</DsButton>
          </div>
        </form>

        <div v-else-if="confirmingDuplicate" class="dc-confirm">
          <p>{{ $t('assistant.draft.duplicateConfirm') }}</p>
          <div class="dc-actions">
            <DsButton variant="feature" size="sm" :loading="busy" @click="doConfirm">{{ $t('assistant.draft.loadAnyway') }}</DsButton>
            <DsButton variant="secondary" size="sm" @click="confirmingDuplicate = false">{{ $t('assistant.draft.cancel') }}</DsButton>
          </div>
        </div>

        <div v-else class="dc-actions">
          <DsButton variant="feature" size="sm" :loading="busy" :disabled="busy" @click="onLoad">
            {{ $t('assistant.draft.load') }}<template #trailing><PhCheck :size="16" /></template>
          </DsButton>
          <DsButton v-if="!isSheet && editFieldsAll.length" variant="secondary" size="sm" :disabled="busy" @click="startEdit">{{ $t('assistant.draft.fix') }}</DsButton>
          <DsButton variant="danger-subtle" size="sm" :disabled="busy" @click="onDiscard">{{ $t('assistant.draft.discard') }}</DsButton>
        </div>
      </template>
    </section>

    <p v-else-if="draft.status === 'confirmed'" class="dc-done">
      <PhCheckCircle :size="18" weight="fill" /> {{ doneText || $t('assistant.draft.done') }}
    </p>
    <p v-else-if="draft.status === 'discarded'" class="dc-muted">{{ $t('assistant.draft.discarded') }}</p>
    <p v-else-if="draft.status === 'failed'" class="dc-error">{{ draft.error || $t('assistant.errors.generic') }}</p>

    <span v-if="busy" class="dc-busy" aria-live="polite">{{ $t('assistant.thinking') }}</span>
  </article>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhArrowSquareOut, PhWarning, PhCheck, PhCheckCircle, PhInfo } from '@phosphor-icons/vue';
import DsButton from '@/components/ds/DsButton.vue';
import DsInput from '@/components/ds/DsInput.vue';
import DsSelect from '@/components/ds/DsSelect.vue';
import { useAssistant, draftOf, BLOCKING_WARNINGS } from '@/composables/useAssistant';
import { moduleConfig, kindConfig } from '@/composables/useAssistantDock';
import DraftSheet from './DraftSheet.vue';
import { shortFileName } from '@/utils/shortFileName';

const UNITS = { kWh: 'kWh', m3: 'm³', 'm³': 'm³', L: 'L' };

export default {
  name: 'DraftCard',
  components: { DsButton, DsInput, DsSelect, DraftSheet, PhArrowSquareOut, PhWarning, PhCheck, PhCheckCircle, PhInfo },
  props: {
    initialDraft: { type: Object, required: true },
    readOnly: { type: Boolean, default: false },
  },
  emits: ['changed', 'navigate'],
  setup(props, { emit }) {
    const { t, te, locale } = useI18n();
    const api = useAssistant();

    const draft = ref(props.initialDraft);
    const busy = ref(false);
    const error = ref('');
    const trail = ref([]);
    const answerModel = ref('');
    const editing = ref(false);
    const confirmingDuplicate = ref(false);
    const doneText = ref('');
    const receiptUrl = ref(props.initialDraft.file_sas_url || '');

    const moduleCfg = computed(() => moduleConfig(draft.value.module));
    const config = computed(() => kindConfig(draft.value.module, draft.value.kind));
    const isSheet = computed(() => config.value.layout === 'sheet');
    const proposal = computed(() => draft.value.proposal || {});
    const reading = computed(() => proposal.value.reading || {});
    const options = computed(() => proposal.value.options || {});
    const question = computed(() => proposal.value.question || null);
    const remaining = computed(() => (proposal.value.missing || []).length);

    // ── Títulos ─────────────────────────────────────────────────────
    const icon = computed(() => config.value.icon);
    const title = computed(() => {
      const k = `assistant.modules.${draft.value.module}.kinds.${draft.value.kind}`;
      return te(k) ? t(k) : t('assistant.draft.genericTitle');
    });

    // ── Formato ─────────────────────────────────────────────────────
    const unitLabel = (u) => UNITS[u] || u || '';
    const num = (n) => new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(n);
    const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
    const money = (v, cur) => { try { return new Intl.NumberFormat(locale.value, { style: 'currency', currency: cur || 'USD' }).format(v); } catch { return `${v} ${cur || ''}`; } };
    const label = (key) => (te(`assistant.fields.${key}`) ? t(`assistant.fields.${key}`) : key);

    const valueLabel = (group, v) => (te(`assistant.values.${group}.${v}`) ? t(`assistant.values.${group}.${v}`) : v);
    // "3,75 h (calculado del horario 8:00–12:00)"
    const hoursText = (r) => {
      const h = `${num(r.totalHourTraining)} h`;
      if (r.hoursSource === 'times') {
        const s0 = (r.sessions || [])[0];
        const range = s0?.start && s0?.end ? `${s0.start}–${s0.end}` : '';
        return `${h} (${t('assistant.draft.hoursFromTimes', { range })})`.replace(' ()', '');
      }
      if (r.hoursSource === 'stated') return `${h} (${t('assistant.draft.hoursStated')})`;
      return h;
    };

    const readingFacts = computed(() => (config.value?.readingFields || []).map((f) => {
      const r = reading.value;
      let value = null;
      if (f.type === 'date') value = r[f.key] ? fmtDate(r[f.key]) : null;
      else if (f.type === 'period') value = r[f.from] && r[f.to] ? `${fmtDate(r[f.from])} – ${fmtDate(r[f.to])}` : null;
      else if (f.type === 'quantity') value = r[f.key] != null ? `${num(r[f.key])} ${unitLabel(r[f.unitFrom])}` : null;
      else if (f.type === 'money') value = r[f.key] != null ? money(r[f.key], r[f.currencyFrom]) : null;
      else if (f.type === 'number') value = r[f.key] != null ? num(r[f.key]) : null;
      else if (f.type === 'hours') value = r[f.key] != null ? hoursText(r) : null;
      else if (f.optionsFrom) value = r[f.key] ? (optLabel(f.optionsFrom, r[f.key]) || r[f.key]) : null;
      else if (f.valueKey) value = r[f.key] ? valueLabel(f.valueKey, r[f.key]) : null;
      else value = r[f.key] || null;
      return value ? { key: f.key, label: label(f.key), value } : null;
    }).filter(Boolean));

    const optLabel = (list, value) => (options.value[list] || []).find(o => String(o.value) === String(value))?.label;
    const targetFacts = computed(() => (config.value?.targetFields || []).map((f) => {
      const src = f.from === 'reading' ? reading.value : (proposal.value.target || {});
      const v = src[f.key];
      if (v == null || v === '') return null;
      const name = f.options ? optLabel(f.options, v) : (f.valueKey ? valueLabel(f.valueKey, v) : v);
      return name ? { key: f.key, label: label(f.label), value: name } : null;
    }).filter(Boolean));

    // Por qué eligió el destino (§ 3). `user`/null: nada.
    const basisText = computed(() => {
      const b = proposal.value.basis;
      const r = reading.value;
      if (b === 'meter_history') {
        const site = optLabel('inventories', proposal.value.target?.inventoryId);
        return r.identifier && site ? t('assistant.basis.meter_history', { meter: r.identifier, site }) : t('assistant.basis.meter_history_short');
      }
      if (b === 'plate' && r.plate) return t('assistant.basis.plate', { plate: r.plate });
      if (b === 'single_option') return t('assistant.basis.single_option');
      return '';
    });

    // ── Pregunta ────────────────────────────────────────────────────
    // Clave del PATCH: la del perfil si difiere; si no, `question.field` tal cual
    // (planillas: `inventory_value:Oficina`, `group:g3:source`…).
    const patchDef = computed(() => {
      const f = question.value?.field;
      if (!f) return null;
      return { key: f, ...(config.value?.patchKeys?.[f] || {}) };
    });
    const qOptions = computed(() => {
      let opts = question.value?.options || [];
      if (!opts.length && patchDef.value?.fallbackOptions) opts = patchDef.value.fallbackOptions.map(v => ({ value: v, label: v }));
      if (!opts.length && question.value?.field === 'water_source') opts = options.value.water_sources || [];
      return [...opts].sort((a, b) => Number(!!b.recent) - Number(!!a.recent));
    });
    // `question.input`: choice · number · date · text. Sin él (contratos viejos),
    // se deduce del campo.
    const qInput = computed(() => {
      if (qOptions.value.length) return null;
      const inp = question.value?.input;
      if (inp && inp !== 'choice') return inp;
      const f = question.value?.field;
      if (f === 'consumption') return 'number';
      if (f === 'date') return 'date';
      return inp === 'choice' ? null : (inp || null);
    });
    const validInput = computed(() => (qInput.value === 'number' ? Number(answerModel.value) > 0 : !!answerModel.value));

    // Muchas opciones → lista con la reciente (o la primera) preseleccionada.
    watch(question, () => {
      answerModel.value = '';
      if (qOptions.value.length > 4) answerModel.value = String((qOptions.value.find(o => o.recent) || qOptions.value[0]).value);
    }, { immediate: true });

    // ── API ─────────────────────────────────────────────────────────
    const replace = (d) => {
      if (!d) return;
      draft.value = d;
      if (d.file_sas_url) receiptUrl.value = d.file_sas_url;
      emit('changed', d);
    };

    const patch = async (changes, trailEntry) => {
      busy.value = true; error.value = '';
      try {
        const res = await api.updateDraft(draft.value.id, changes);
        if (res.ok) { if (trailEntry) trail.value.push(trailEntry); editing.value = false; replace(draftOf(res)); }
        else error.value = res.message || t('assistant.errors.generic');
      } catch { error.value = t('assistant.errors.generic'); } finally { busy.value = false; }
    };

    const answer = (value, shown) => {
      const def = patchDef.value;
      if (!def) return;
      const v = qInput.value === 'number' ? Number(value) : value;
      patch({ [def.key]: v }, { q: question.value.text, a: shown ?? String(value) });
    };
    const answerSelect = () => {
      const o = qOptions.value.find(x => String(x.value) === String(answerModel.value));
      if (o) answer(o.value, o.label);
    };
    const answerInput = () => {
      const v = answerModel.value;
      const shown = qInput.value === 'number' ? `${num(Number(v))} ${question.value.field === 'consumption' ? unitLabel(reading.value.unit) : ''}`.trim()
        : qInput.value === 'date' ? fmtDate(v) : v;
      answer(v, shown);
    };

    // ── Corregir ────────────────────────────────────────────────────
    const edit = reactive({});
    const sourceOf = (f) => (f.from === 'target' ? proposal.value.target || {} : reading.value);
    const editFieldsAll = computed(() => config.value?.editFields || []);
    const visibleEditFields = computed(() => editFieldsAll.value.filter((f) => {
      if (f.onlyKind && f.onlyKind !== draft.value.kind) return false;
      if (f.options && !(options.value[f.options] || []).length) return false;
      if (f.onlyWhen) {
        const sel = (options.value[f.onlyWhen.options] || []).find(o => String(o.value) === String(edit[f.onlyWhen.field]));
        return sel?.[f.onlyWhen.optionKey] === f.onlyWhen.equals;
      }
      return true;
    }));
    const editOptions = (f) => {
      if (f.static) return f.static.map(v => ({ value: v, label: v }));
      let list = options.value[f.options] || [];
      if (f.filterBy) {
        const by = list.filter(o => String(o[f.filterBy.optionKey]) === String(edit[f.filterBy.field]));
        if (by.length) list = by;
      }
      return list;
    };
    const startEdit = () => {
      (config.value?.editFields || []).forEach((f) => {
        const v = sourceOf(f)[f.key];
        edit[f.key] = v == null ? '' : String(v).slice(0, f.input === 'date' ? 10 : undefined);
      });
      editing.value = true;
    };
    const saveEdit = () => {
      const changes = {};
      visibleEditFields.value.forEach((f) => {
        const before = sourceOf(f)[f.key];
        const now = edit[f.key];
        if (now === '' || String(now) === String(f.input === 'date' ? String(before || '').slice(0, 10) : before ?? '')) return;
        changes[f.key] = f.number || f.input === 'number' ? Number(now) : now;
      });
      if (!Object.keys(changes).length) { editing.value = false; return; }
      patch(changes, { q: t('assistant.draft.fixedIt'), a: '' });
    };

    // ── Cargar / Descartar ──────────────────────────────────────────
    const onLoad = () => {
      if ((draft.value.warnings || []).some(w => BLOCKING_WARNINGS.has(w.code))) { confirmingDuplicate.value = true; return; }
      doConfirm();
    };
    const doConfirm = async () => {
      if (busy.value) return;
      busy.value = true; error.value = '';
      try {
        const res = await api.confirmDraft(draft.value.id);
        if (res.ok) {
          const record = res.data?.record || {};
          const kind = res.data?.draft?.record_kind;
          const failed = (record.failed || []).length;
          if (kind === 'staff_roster') doneText.value = t('assistant.draft.doneRoster', { created: record.created || 0, updated: record.updated || 0 });
          else if (kind === 'consumption_log') doneText.value = t('assistant.draft.doneLog', { created: record.created || 0 });
          else if (kind === 'training') doneText.value = t('assistant.draft.doneTraining');
          else if (kind !== 'water' && record.tco2e != null) doneText.value = t('assistant.draft.doneCo2', { kg: num(record.tco2e * 1000) });
          else doneText.value = t('assistant.draft.done');
          if (failed) doneText.value += ` ${t('assistant.draft.doneFailed', { n: failed }, failed)}`;
          replace({ ...draft.value, ...(res.data?.draft || {}), status: 'confirmed' });
        } else if (res.code === 'DRAFT_INCOMPLETE' && res.data?.draft) {
          replace(res.data.draft);
        } else if (res.code === 'DRAFT_NOT_PENDING') {
          error.value = t('assistant.draft.notPending');
          const again = await api.getDraft(draft.value.id);
          if (again.ok) replace(draftOf(again));
        } else {
          error.value = res.message || t('assistant.errors.generic');
          if (res.code === 'DRAFT_TARGET_INVALID') startEdit();
        }
      } catch { error.value = t('assistant.errors.generic'); } finally {
        busy.value = false;
        confirmingDuplicate.value = false;
      }
    };
    const onDiscard = async () => {
      if (busy.value) return;
      busy.value = true; error.value = '';
      try {
        const res = await api.discardDraft(draft.value.id);
        if (res.ok) replace({ ...draft.value, status: 'discarded' });
        else error.value = res.message || t('assistant.errors.generic');
      } finally { busy.value = false; }
    };

    // El enlace temporal al archivo solo viene en el detalle.
    if (!receiptUrl.value && draft.value.id) {
      api.getDraft(draft.value.id).then((res) => { if (res.ok) receiptUrl.value = draftOf(res)?.file_sas_url || ''; });
    }

    return {
      BLOCKING_WARNINGS, shortFileName,
      draft, busy, error, trail, answerModel, editing, confirmingDuplicate, doneText, receiptUrl,
      config, moduleCfg, isSheet, reading, question, remaining, icon, title, readingFacts, targetFacts, basisText,
      qOptions, qInput, validInput, unitLabel,
      answer, answerSelect, answerInput,
      edit, editFieldsAll, visibleEditFields, editOptions, startEdit, saveEdit, patch,
      onLoad, doConfirm, onDiscard,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/assistant/draft-card";
</style>
